import type { ApiClient } from './client';

/**
 * The organization's machine credentials, as `veri-plus-backend` serves them.
 *
 * `routes/v1/product.routes.ts`, mounted at `/v1/api-keys`, every route
 * behind `requireOrgAdmin`, so a member gets 403 `insufficient_role`:
 * `POST /` with `{ name }`, 1 to 120 characters, answering 201
 * `{ apiKey, key }`; `GET /`, no paging, answering `{ apiKeys }` newest first
 * with revoked keys included; `DELETE /:apiKeyId`, answering 204, or 404
 * `api_key_not_found` for an unknown or already revoked key.
 *
 * A key is `apiKey.model.ts` through its `toJSON`, which drops the hash. The
 * plaintext `key` is in the create answer and nowhere else, ever: the list
 * has no field for it, and this module returns it apart from the record so a
 * caller cannot store it in the list by accident. D16.
 *
 * Create and revoke are behind `requireStepUp`, `create_api_key` and
 * `revoke_api_key`: the first ask answers 202 `{ challengeId }` and mails a
 * code, and the same request repeated with `X-Otp-Challenge` and
 * `X-Otp-Code` does the work. Each call here takes the answer, or none, and
 * says which it got. D41.
 */

export interface ApiKey {
  id: string;
  organizationId: string;
  name: string;
  /** The key's first twelve characters, `vpk_` and eight hex. */
  prefix: string;
  createdBy: string;
  lastUsedAt?: string;
  revokedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const apiKeysPath = '/v1/api-keys';

export async function listApiKeys(api: ApiClient, signal?: AbortSignal): Promise<ApiKey[]> {
  const answer = await api.get<{ apiKeys?: ApiKey[] }>(apiKeysPath, signal ? { signal } : {});
  return answer.apiKeys ?? [];
}

/** A mailed code, answering the challenge the first ask was given. */
export interface StepUpAnswer {
  challengeId: string;
  code: string;
}

/** The API stopped the request until a mailed code is answered. */
export interface StepUpChallenge {
  kind: 'challenge';
  challengeId: string;
}

const answered = (answer?: StepUpAnswer) =>
  answer ? { headers: { 'X-Otp-Challenge': answer.challengeId, 'X-Otp-Code': answer.code } } : {};

const challengeOf = (body: unknown): StepUpChallenge | null =>
  typeof body === 'object' &&
  body !== null &&
  typeof (body as { challengeId?: unknown }).challengeId === 'string'
    ? { kind: 'challenge', challengeId: (body as { challengeId: string }).challengeId }
    : null;

/** The record for the list, and the secret, shown once; or the challenge. */
export async function createApiKey(
  api: ApiClient,
  name: string,
  answer?: StepUpAnswer,
): Promise<StepUpChallenge | { kind: 'created'; apiKey: ApiKey; secret: string }> {
  const body = await api.post<unknown>(apiKeysPath, { name }, answered(answer));
  const challenge = challengeOf(body);
  if (challenge) return challenge;
  const made = body as { apiKey: ApiKey; key: string };
  return { kind: 'created', apiKey: made.apiKey, secret: made.key };
}

export async function revokeApiKey(
  api: ApiClient,
  apiKeyId: string,
  answer?: StepUpAnswer,
): Promise<StepUpChallenge | { kind: 'revoked' }> {
  const body = await api.delete<unknown>(
    `${apiKeysPath}/${encodeURIComponent(apiKeyId)}`,
    answered(answer),
  );
  return challengeOf(body) ?? { kind: 'revoked' };
}

/** The board's `vpk_9f3a2c1b…`: the stored prefix and an ellipsis. */
export const maskedKey = (key: ApiKey) => `${key.prefix}…`;

const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/** `2 Aug 2026`, drawn. */
export const fullDate = (at: Date) =>
  `${at.getDate()} ${months[at.getMonth()]} ${at.getFullYear()}`;

/** The Created column: `Today` and `2 Aug 2026` are drawn. D16. */
export function createdWhen(iso: string, now: Date): string {
  const at = new Date(iso);
  return sameDay(at, now) ? 'Today' : fullDate(at);
}

const hour = 3600_000;

/**
 * The Last used column: `Never`, `2 hours ago` and `30 Aug 2026` are drawn.
 * Under a day is hours, under an hour is minutes, then the date. D16.
 */
export function lastUsedWhen(iso: string | undefined, now: Date): string {
  if (iso === undefined) return 'Never';
  const at = new Date(iso);
  const age = now.getTime() - at.getTime();
  if (age >= 24 * hour) return fullDate(at);
  if (age >= hour) {
    const h = Math.floor(age / hour);
    return `${h} ${h === 1 ? 'hour' : 'hours'} ago`;
  }
  const m = Math.max(1, Math.floor(age / 60_000));
  return `${m} ${m === 1 ? 'minute' : 'minutes'} ago`;
}
